import { Injectable } from '@angular/core'

export const ADOBE_SUBPAGE_VIEWS = {
  modal_download_material_open: 'Download Materials Form: Open',
  modal_download_material_complete: 'Download Materials Form: Complete',
  modal_download_material_cancel: 'Download Materials Form: Cancel'
};

export const LEARNING_LIBRARY_SUBPAGE_VIEWS = {
  lesson: 'Learning Library: Lesson',
  quiz_intro: 'Learning Library: Quiz Intro',
  quiz_questions: 'Learning Library: Quiz Questions',
  quiz_complete: 'Learning Library: Quiz Complete',
} as const;

export type LearningLibrarySubPage = keyof typeof LEARNING_LIBRARY_SUBPAGE_VIEWS;

export enum Analytics {
  PAGE_TEMPLATE_LGN = 'login',
}

interface AdobeLaunch {
  initializePage(page: any, environment: string): void
}

interface DataLayerEvent {
  [key: string]: any
  event: string
}

declare global {
  interface Window {
    adobeLaunch: AdobeLaunch
    dataLayer: DataLayerEvent[]
  }
}

export enum AdobeLaunchEvent {
  SUBPAGE_VIEWED = 'subPageViewed',
  CONTENT_DOWNLOADED = 'contentDownloaded',
  PAGE_LOADED = 'pageLoaded'
};

@Injectable({
  providedIn: 'root',
})
export class AdobeLaunchService {

  constructor(
  ) {}

  /**
   * Used to reset the data layer by removing only the events this service
   * previously pushed. Third-party events (e.g. OneTrust's
   * `OneTrustGroupsUpdated`, which is only pushed once when the CMP script
   * initializes on a hard page load) are preserved so they aren't wiped out
   * on subsequent SPA navigations.
   */
  public resetDataLayer() {
    if (window.dataLayer) {
      const appEventNames: string[] = Object.values(AdobeLaunchEvent)
      const preservedEvents = window.dataLayer.filter(
        (entry) => !appEventNames.includes(entry.event)
      )
      // Mutate the existing array in place instead of reassigning
      // `window.dataLayer` to a new array. Reassigning would sever the
      // reference that Adobe Launch/GTM's loader script attaches its
      // push listener to, breaking event tracking on subsequent pushes.
      // Repopulate via index assignment + length truncation (not `push`),
      // since Adobe Launch/GTM commonly wrap `push` to react to new
      // entries. Calling `push` here would re-trigger that processing for
      // every preserved event, causing duplicate tracking.
      window.dataLayer.splice(0, window.dataLayer.length, ...preservedEvents)
    }
  }

  private subPageViewed(subPageViewedDetails: {
    subPageID: string
    spClicked: string
  }) {
    //Used to signify that a subPage was activated/viewed
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: AdobeLaunchEvent.SUBPAGE_VIEWED,//e166
      subPage: {
        subPageID: subPageViewedDetails.subPageID, //v105
        spClicked: subPageViewedDetails.spClicked  
      }
    });
  }

  private pageViewed(dataObject) {
    //Used to signify that a new page is being loaded
    this.resetDataLayer();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: AdobeLaunchEvent.PAGE_LOADED,
        page: {
            pageTitle: dataObject.pageTitle, //c30
            pageName: dataObject.pageName, //c18, v18
            siteSection1: dataObject.siteSection1, //c38
            siteSection2: dataObject.siteSection2, //c39
            siteIdentifier: dataObject.siteIdentifier,
            pageTemplate: dataObject.pageTemplate,//v157
            is404:"",
            DLVersion: "" //c42, v42       
        }
    });  
    window.dataLayer.push({
      "event": "pageLoadCompleted"
    });
  }

  public contentDownloaded(fileDetails: {
    fileName: string
    fileType: string
    fileURL: string
  }) {
      //Use to track the download of content
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: AdobeLaunchEvent.CONTENT_DOWNLOADED, //e24
        linkInfo: {
          fileName: fileDetails.fileName, //v70
          fileType: fileDetails.fileType,
          fileURL: fileDetails.fileURL, //v63
      }
    });
  }

  public trackSubPage(pageIndex: string) {
    let subPageID: string ='';
    let spClicked: string ='true';

    switch(pageIndex) {
      //Download Materials Form: Open
      case 'modal_download_material_open':
        subPageID = ADOBE_SUBPAGE_VIEWS.modal_download_material_open;
        break;

      //Download Materials Form: Complete
      case 'modal_download_material_complete':
        subPageID = ADOBE_SUBPAGE_VIEWS.modal_download_material_complete;
        break;

      //Download Materials Form: Cancel
      case 'modal_download_material_cancel':
        subPageID = ADOBE_SUBPAGE_VIEWS.modal_download_material_cancel;
        break;
    }

    this.subPageViewed({
      subPageID: subPageID,
      spClicked: spClicked
    })

  }

  /**
   * Tracks a Learning Library sub-page view (lesson or quiz step).
   * Produces a subPageID like "Learning Library: Lesson - Budget Basics".
   */
  public trackLearningLibrarySubPage(
    subPage: LearningLibrarySubPage,
    lessonTitle?: string
  ) {
    const base = LEARNING_LIBRARY_SUBPAGE_VIEWS[subPage];
    this.subPageViewed({
      subPageID: lessonTitle ? `${base} - ${lessonTitle}` : base,
      spClicked: 'true',
    });
  }

  public trackPageView(dataObject) {
    this.pageViewed(dataObject);
  }

}
